/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import _ from 'lodash';

import ApiCalls from 'app/utils/apiCalls';
import { GetStudiesFilters, StudyStore } from 'app/interfaces/StudyStore';
import { Base64Image, ImageLoader } from 'app/interfaces/ImageLoader';
import { IDisplayableImageEncoder } from 'app/interfaces/IDisplayableImageEncoder';
import { Patient } from 'app/interfaces/Patient';
import { WMExam, WMPatient, WMStudyStore } from 'app/dicom/WorklistMonitor';

/**
 * @class
 * @implements {StudyStore}
 */
export default class CloudBasedStudyStore implements StudyStore, WMStudyStore {
  constructor(private imageLoader: ImageLoader, private imageEncoder: IDisplayableImageEncoder) {}

  updateStudyMetadata: StudyStore['updateStudyMetadata'] = async (studyId, data) => {
    await ApiCalls.updateStudyMetadata(studyId, data);
  };

  updatePatient = (studyId: string, patient: Patient) => {
    if (patient._id) {
      return ApiCalls.linkStudyToAnimal(studyId, patient._id).then(() => patient);
    }

    return ApiCalls.savePatientInfo(patient).then(({ data }) =>
      ApiCalls.linkStudyToAnimal(studyId, data.animal._id).then(() => data.animal)
    );
  };

  deleteImage = async (studyId: string, imageId: string) => {
    await ApiCalls.removeImagesFromStudy(studyId, [imageId]);
  };

  updateThumbnailImage = async (imageId: string, thumbnailBlob: Blob) => {
    // ApiCalls.saveThumbnail(imageId, thumbnailBlob);
  };

  getStudy = async (studyId: string) => {
    return (await ApiCalls.getStudies([studyId]))[0];
  };

  getStudies = async (studyIds: string[]) => {
    return await ApiCalls.getStudies(studyIds);
  };

  filterStudies = async (filters: GetStudiesFilters) => {
    const response = await ApiCalls.filterStudies(filters);

    return response.data.studies;
  };

  loadImage: StudyStore['loadImage'] = async (studyId, imageId) => {
    const base64ImageResponse = await ApiCalls.getImage({ imageId });
    const imageFile: Base64Image = `data:image/png;base64,${base64ImageResponse.data.image}`;

    return this.imageLoader.load(imageFile);
  };

  loadThumbnail: StudyStore['loadThumbnail'] = async () => {
    throw new Error('No THumbnail found');
  };

  updateReport: StudyStore['updateReport'] = async (studyId, comment, isCommentDirty) => {
    await ApiCalls.sendComplementaryInfo({
      study_id: studyId,
      comment,
      isCommentDirty,
    });
  };

  updateImage: StudyStore['updateImage'] = async (studyId, imageId, image) => {
    await ApiCalls.updateImage(studyId, imageId, image);
  };

  saveRawImage: StudyStore['saveRawImage'] = async () => {
    throw new Error('Not raw storage available for CloudBasedStore');
    // We do not store raw image inside the server.
  };

  saveLastProcessedImage: StudyStore['saveLastProcessedImage'] = async (
    studyId,
    imageId,
    lastProcessedImage
  ) => {
    const { imageData } = await this.imageLoader.load(lastProcessedImage);
    const imageBlob = await this.imageEncoder.toJPEG(imageData);

    await ApiCalls.putImage(imageId, imageBlob);
  };

  createStudyFromWorklist = async (patientInfo: WMPatient, exam: WMExam): Promise<void> => {
    await ApiCalls.importPACSWorklistExam(patientInfo, exam);
  };
}
